import { cn } from '@/lib/utils';

interface AxisLogoProps {
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
}

const sizeMap = {
  sm: 'w-14 h-14',
  md: 'w-20 h-20',
  lg: 'w-28 h-28',
  xl: 'w-36 h-36',
  '2xl': 'w-48 h-48',
};

/**
 * Axis Datum Logo — Compass PNG
 */
const AxisLogo = ({ size = 'md', className }: AxisLogoProps) => {
  return (
    <div
      className={cn(
        sizeMap[size],
        'relative flex items-center justify-center shrink-0',
        className
      )}
    >
      <img
        src="/images/axis-datum-compass.png"
        alt="Axis Datum"
        className="w-full h-full object-contain"
        draggable={false}
      />
    </div>
  );
};

/**
 * Axis Datum Wordmark — "AXIS" large, "datum" small, diagonal layout
 */
interface AxisWordmarkProps {
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
}

const axisSizeMap = {
  sm: { axis: 'text-xl', datum: 'text-[10px]', gap: 'ml-3' },
  md: { axis: 'text-2xl', datum: 'text-xs', gap: 'ml-4' },
  lg: { axis: 'text-4xl', datum: 'text-sm', gap: 'ml-5' },
  xl: { axis: 'text-5xl', datum: 'text-base', gap: 'ml-6' },
  '2xl': { axis: 'text-7xl', datum: 'text-xl', gap: 'ml-8' },
};

const AxisWordmark = ({ size = 'md', className }: AxisWordmarkProps) => {
  const sizes = axisSizeMap[size];
  return (
    <span className={cn('inline-flex flex-col leading-none', className)}>
      <span className={cn('font-brand italic font-semibold tracking-wide text-gray-900 dark:text-secondary-100', sizes.axis)}>
        Axis
      </span>
      <span className={cn('font-medium tracking-[0.2em] text-gray-400 dark:text-secondary-400 self-end -mt-0.5', sizes.datum, sizes.gap)}>
        datum
      </span>
    </span>
  );
};

/**
 * Axis Datum Logo with wordmark — Full brand mark
 */
interface AxisLogoFullProps {
  size?: 'sm' | 'md' | 'lg';
  showTagline?: boolean;
  className?: string;
}

const AxisLogoFull = ({ size = 'md', showTagline = false, className }: AxisLogoFullProps) => {
  return (
    <div className={cn('flex items-center gap-2.5', className)}>
      <AxisLogo size={size} />
      <div className="flex flex-col">
        <AxisWordmark size={size} />
        {showTagline && (
          <span className="text-[10px] font-brand italic text-secondary-500 tracking-wide mt-1">Where Operations Align.</span>
        )}
      </div>
    </div>
  );
};

// Backward-compatible exports
const CoreOpsLogo = AxisLogo;
const CoreOpsLogoFull = AxisLogoFull;

export { AxisLogo, AxisWordmark, AxisLogoFull, CoreOpsLogo, CoreOpsLogoFull };
